import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { IFormData } from "../Forms/formFields";
import { Feature } from "ol";
import { ISummaryParams } from "../Registration/SummarySection";
import { useAppContext } from "./AppContext";
import { IEsriCodedValue } from "../Map/rest";
import { config } from "../Map/config";
import { IFullscreenViewOverlay } from "../Registration/Registration";
import { DrawType, PageId } from "../Map/enums";
import { generateSnapshots } from "../Map/snapshot";
import { getDrawingSymbol } from "../Map/symbols";
import { ILVMForestryData } from "../interfaces";

export interface IRegistrationContext {
    userConsent: boolean;
    setUserConsent: Dispatch<SetStateAction<boolean>>;
    validateForm: string;
    setValidateForm: (timeStamp: string) => void;
    resetForm: number;
    setResetForm: Dispatch<SetStateAction<number>>;
    rawData: IFormData;
    setRawData: Dispatch<SetStateAction<IFormData>>;
    formFeature: Feature;
    setFormFeature: Dispatch<SetStateAction<Feature>>;
    attachments: FileList;
    setAttachments: Dispatch<SetStateAction<FileList>>;
    summaryData: ISummaryParams;
    setSummaryData: Dispatch<SetStateAction<ISummaryParams>>;
    getSummaryJson: () => Promise<any>;
    overlay: IFullscreenViewOverlay;
    setOverlay: Dispatch<SetStateAction<IFullscreenViewOverlay>>;
    options: ISelectOptions;
    setOptions: Dispatch<SetStateAction<ISelectOptions>>;
    page: PageId;
    setPage: Dispatch<SetStateAction<PageId>>;
    onFormBack: () => void;
    onFormProceed: () => void;
    onFormUpdated: () => void;
    onValidateForm: (pageId: PageId) => void;
    proceedClicked: boolean;
    setProceedClicked: Dispatch<SetStateAction<boolean>>;
    drawingMenuVisible: boolean;
    setDrawingMenuVisible: Dispatch<SetStateAction<boolean>>;
    toggleDrawingMenuVisible: () => void;
    drawingInfoVisible: boolean;
    setDrawingInfoVisible: Dispatch<SetStateAction<boolean>>;
    conditionAgreed: boolean;
    setConditionAgreed: Dispatch<SetStateAction<boolean>>;
    snapshot: string;
    setSnapshot: Dispatch<SetStateAction<string>>;
    drawnFeatures: Feature[];
    setDrawnFeatures: Dispatch<SetStateAction<Feature[]>>;
    restartApp: () => void;
    willAttachDrawing: boolean;
    setWillAttachDrawing: Dispatch<SetStateAction<boolean>>;
    LvmForestryData: ILVMForestryData;
    setLvmForestryData: Dispatch<SetStateAction<ILVMForestryData>>;
    applicationId: string;
    setApplicationId: Dispatch<SetStateAction<string>>;
}

interface ISelectOptions {
    [fieldName: string]: IEsriCodedValue[];
}

const RegistrationContext = createContext<IRegistrationContext>(null);

export const useRegistrationContext = (): IRegistrationContext => useContext(RegistrationContext);

export const RegistrationContextProvider: FC = (props: React.PropsWithChildren<IRegistrationContext>) => {
    const [userConsent, setUserConsent] = useState<boolean>(false);
    const [conditionAgreed, setConditionAgreed] = useState<boolean>(false);
    const [validateForm, setValidateForm] = useState<string>("1");
    const [resetForm, setResetForm1] = useState<number>(0);
    const [rawData, setRawData] = useState({});
    const [formFeature, setFormFeature] = useState<Feature>(null);
    const [attachments, setAttachments] = useState<FileList>(null);
    const [summaryData, setSummaryData] = useState<ISummaryParams>();
    const [overlay, setOverlay] = useState<IFullscreenViewOverlay>(null);
    const [options, setOptions] = useState<ISelectOptions>({});
    const [page, setPage] = useState<PageId>(PageId.ApplicantForm);
    // const [page, setPage] = useState<PageId>(PageId.DrawingForm);
    const [proceedClicked, setProceedClicked] = useState<boolean>(false);
    const [drawingMenuVisible, setDrawingMenuVisible] = useState<boolean>(true);
    const [drawingInfoVisible, setDrawingInfoVisible] = useState<boolean>(false);
    const [snapshot, setSnapshot] = useState<string>();
    const [drawnFeatures, setDrawnFeatures] = useState<Feature[]>([]);
    const [willAttachDrawing, setWillAttachDrawing] = useState<boolean>(false);
    const [LvmForestryData, setLvmForestryData] = useState<ILVMForestryData>();
    const [applicationId, setApplicationId] = useState<string>();

    const drawingMenuVisibleRef = useRef<boolean>(true);

    const { basemap, getFieldValue, getFieldAlias, getDateFormated, fieldInfos, setHasMap } = useAppContext();

    useEffect(() => {
        const target = document.getElementById("pubevents-map");
        page === PageId.ApplicantForm
            ? target.classList.add("recaptcha-visible")
            : target.classList.remove("recaptcha-visible");
    }, [page]);

    useEffect(() => {
        if (!fieldInfos) {
            return;
        }
        const updatedOptions = {};
        config.forms.eventForm.fields.map((inputField) => {
            const field = inputField.field && fieldInfos.find((info) => info.name === inputField.field);
            if (!(field && field.domain && field.domain.codedValues)) {
                return;
            }

            updatedOptions[inputField.field] =
                field.name === "EVENTTYPE"
                    ? field.domain.codedValues.filter((cv) => !(cv.code === 9 || cv.code === 10))
                    : field.name === "ROUTEMARKERTYPE"
                    ? field.domain.codedValues.filter((cv) => !(cv.code === 2 || cv.code === 3))
                    : field.domain.codedValues;
        });
        setOptions(updatedOptions);
    }, [fieldInfos]);

    useEffect(() => {
        LvmForestryData && setApplicationId(getSummaryId(formFeature.get("OBJECTID"), LvmForestryData));
    }, [LvmForestryData]);

    const prepareToResetForm = () => {
        setRawData({});
        setFormFeature(null);
        setValidateForm("1");
        setAttachments(null);
        setResetForm1(Date.now());
        setSummaryData(null);
        setDrawnFeatures([]);
        setSnapshot(null);
        setWillAttachDrawing(false);
        setLvmForestryData(undefined);
        resetMinMaxDates();
    };
    const restartApp = () => {
        setUserConsent(false);
        setConditionAgreed(false);
        setValidateForm("1");
        setResetForm1(0);
        setRawData({});
        setFormFeature(null);
        setAttachments(null);
        setSummaryData(null);
        setOverlay(null);
        setPage(PageId.ApplicantForm);
        setProceedClicked(false);
        setDrawingMenuVisible(true);
        setDrawingInfoVisible(false);
        setSnapshot(null);
        setDrawnFeatures([]);
        setWillAttachDrawing(false);
        setLvmForestryData(undefined);
        resetMinMaxDates();
    };

    const getSnapshots = useCallback(async () => {
        drawnFeatures && drawnFeatures.map((f) => f.setStyle(getDrawingSymbol(f.get("DRAWTYPE"))));
        return await generateSnapshots(drawnFeatures, basemap, setSnapshot);
    }, [drawnFeatures]);

    const getSummaryJson = async (): Promise<ISummaryParams> => {
        const attachedFiles = attachments
            ? {
                  title: "Pievienotie dokumenti:",
                  rows: Array.from(attachments, (attachment) => attachment.name)
              }
            : undefined;

        const summary: ISummaryParams = {
            customer: getCustomerSection(formFeature),
            event: getEventSection(formFeature, getFieldAlias, getFieldValue),
            place: getPlaceSection(drawnFeatures, LvmForestryData.LVMForestryName),
            globalId: applicationId,
            attachments: attachedFiles,
            date: getDateFormated(Date.now())
        };

        drawnFeatures && drawnFeatures.length && (summary.snapshot = await getSnapshots());

        return summary;
    };

    const onFormBack = useCallback(() => {
        if (page === PageId.EventForm) {
            setUserConsent(false);
            setOverlay(null);
            setValidateForm("1");
            setPage(PageId.ApplicantForm);
        } else if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setLvmForestryData(undefined);
            setValidateForm("1");
            setPage(PageId.EventForm);
        } else if (page === PageId.ConditionsForm) {
            setOverlay(null);
            setValidateForm("1");
            setConditionAgreed(false);
            setPage(PageId.DrawingForm);
        } else if (page === PageId.SummaryForm) {
            setPage(PageId.ConditionsForm);
        }
    }, [page]);

    const onFormProceed = useCallback(() => {
        if (page === PageId.Submit) {
            restartApp();
            return;
        }
        setProceedClicked(true);
        onValidateForm(page);
        if (page === PageId.ConditionsForm || page === PageId.SummaryForm) {
            // TODO deselect drawn features, when proceed in drawing form
            onFormUpdated();
        }
    }, [page]);

    const onFormUpdated = useCallback(() => {
        if (page === PageId.ApplicantForm) {
            setPage(PageId.EventForm);
        } else if (page === PageId.EventForm) {
            setPage(PageId.DrawingForm);
            setHasMap(true);
        } else if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setPage(PageId.ConditionsForm);
        } else if (page === PageId.ConditionsForm) {
            setPage(PageId.SummaryForm);
        } else if (page === PageId.SummaryForm) {
            setPage(PageId.Submit);
        }
    }, [page]);

    const onValidateForm = (id: PageId) => {
        setValidateForm(id + "-" + Date.now());
    };

    const toggleDrawingMenuVisible = () => {
        drawingMenuVisibleRef.current = !drawingMenuVisibleRef.current;
        setDrawingMenuVisible(drawingMenuVisibleRef.current);
    };

    const values = {
        userConsent,
        setUserConsent,
        validateForm,
        setValidateForm,
        resetForm,
        setResetForm: prepareToResetForm,
        rawData,
        setRawData,
        formFeature,
        setFormFeature,
        attachments,
        setAttachments,
        summaryData,
        setSummaryData,
        getSummaryJson,
        overlay,
        setOverlay,
        options,
        setOptions,
        page,
        setPage,
        onFormBack,
        onFormProceed,
        onFormUpdated,
        onValidateForm,
        proceedClicked,
        setProceedClicked,
        drawingMenuVisible,
        setDrawingMenuVisible,
        toggleDrawingMenuVisible,
        drawingInfoVisible,
        setDrawingInfoVisible,
        conditionAgreed,
        setConditionAgreed,
        snapshot,
        setSnapshot,
        drawnFeatures,
        setDrawnFeatures,
        restartApp,
        willAttachDrawing,
        setWillAttachDrawing,
        LvmForestryData,
        setLvmForestryData,
        applicationId,
        setApplicationId
    };

    return <RegistrationContext.Provider value={values}>{props.children}</RegistrationContext.Provider>;
};

const getPlaceSection = (drawnFeatures: Feature[], LVMRegionName: string) => {
    const points =
        drawnFeatures.length &&
        drawnFeatures.filter((f) => {
            const drawtype = f.get("DRAWTYPE");
            return (
                drawtype === DrawType.PointCarPark ||
                drawtype === DrawType.PointGathering ||
                drawtype === DrawType.PointInfra ||
                drawtype === DrawType.PointTent ||
                drawtype === DrawType.PointOther
            );
        });
    const lines = drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.Polyline);
    const areas = drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.Polygon);
    const rows = [];

    rows.push({ alias: "LVM reģions: ", value: LVMRegionName });

    points.length &&
        rows.push(
            {
                alias: "Punktveida objekti: ",
                value: points.length + (points.length === 1 ? " objekts" : " objekti")
            },
            ...points.map((point) => ({
                alias: drawTypeLabel[point.get("DRAWTYPE")],
                value: point.get("DESCRIPTION") ? " - " + point.get("DESCRIPTION") : ""
            }))
        );
    lines.length &&
        rows.push(
            {
                alias: "Līnijveida objekti: ",
                value: lines.length + (lines.length === 1 ? " objekts" : " objekti")
            },
            ...lines.map((line) => ({
                alias: "",
                value: line.get("DESCRIPTION") || "bez nosaukuma"
            }))
        );
    areas.length &&
        rows.push(
            {
                alias: "Laukumveida objekti: ",
                value: areas.length + (areas.length === 1 ? " objekts" : " objekti")
            },
            ...areas.map((area) => ({
                alias: "",
                value: area.get("DESCRIPTION") || "bez nosaukuma"
            }))
        );

    return { title: "Pasākuma norises vieta", rows };
};

const getCustomerSection = (formFeature: Feature) => {
    const rows = [
        formFeature.get("APPLICANTNAME"),
        formFeature.get("APPLICANTADDRESS"),
        formFeature.get("APPLICANTPHONE"),
        formFeature.get("APPLICANTEMAIL")
    ];
    const isCompany = formFeature.get("APPLICANTTYPE").toString() === "1";
    isCompany && rows.splice(1, 0, formFeature.get("APPLICANTCODE"));
    isCompany && rows.push(formFeature.get("CONTACTPERSON"), formFeature.get("CONTACTPERSONPHONE"));

    return { title: "Dati par iesniedzēju", rows };
};

const getEventSection = (formFeature: Feature, getFieldAlias: CallableFunction, getFieldValue: CallableFunction) => {
    const rows = [
        { alias: getFieldAlias("EVENTNAME") + ": ", value: getFieldValue(formFeature, "EVENTNAME") },
        { alias: getFieldAlias("EVENTTYPE") + ": ", value: getFieldValue(formFeature, "EVENTTYPE") },
        formFeature.get("EVENTTYPE") === "1"
            ? { alias: "Mācību veids: ", value: getFieldValue(formFeature, "LEARNINGTYPE") }
            : undefined,
        {
            alias: "Norise: ",
            value: getFieldValue(formFeature, "DATEFROM") + "-" + getFieldValue(formFeature, "DATETO")
        },
        formFeature.get("ISFORESTNIGHT") === 1
            ? { alias: "Nakšņošana mežā, naktis: ", value: getFieldValue(formFeature, "NIGHTSCOUNT") }
            : undefined,
        formFeature.get("ISROUTEMARKED") === 1
            ? { alias: getFieldAlias("ISROUTEMARKED") + ": ", value: getFieldValue(formFeature, "ISROUTEMARKED") }
            : undefined,
        formFeature.get("ISROUTEMARKED") === 1
            ? { alias: getFieldAlias("ROUTEMARKERTYPE") + ": ", value: getFieldValue(formFeature, "ROUTEMARKERTYPE") }
            : undefined,
        formFeature.get("ROADCLOSURE") === 1
            ? { alias: getFieldAlias("ROADCLOSURE") + ": ", value: getFieldValue(formFeature, "ROADCLOSURE") }
            : undefined,
        formFeature.get("OTHERELEMENTS") === 1
            ? { alias: getFieldAlias("OTHERELEMENTS") + ": ", value: getFieldValue(formFeature, "OTHERELEMENTS") }
            : undefined,
        {
            alias: getFieldAlias("RESPONSIBLEPERSON") + ": ",
            value:
                getFieldValue(formFeature, "RESPONSIBLEPERSON") +
                ", " +
                getFieldValue(formFeature, "RESPONSIBLEPERSONPHONE")
        }
    ];

    const visitorNumber = getFieldValue(formFeature, "VISITORNUMBER");
    visitorNumber && rows.splice(4, 0, { alias: getFieldAlias("VISITORNUMBER") + ": ", value: visitorNumber });

    const additionalInfo = getFieldValue(formFeature, "ADDITIONALINFO");
    additionalInfo && rows.push({ alias: getFieldAlias("ADDITIONALINFO") + ": ", value: additionalInfo });

    return { title: "Pasākuma apraksts", rows };
};

const drawTypeLabel = {
    [DrawType.PointGathering]: "Pulcēšanās vieta",
    [DrawType.PointCarPark]: "Auto novietošana",
    [DrawType.PointInfra]: "Infrastruktūras punkts",
    [DrawType.PointTent]: "Nakšņošanas vieta",
    [DrawType.PointOther]: "Citas nozīmes punkts",
    [DrawType.Polyline]: "Līnijveida objekts",
    [DrawType.Polygon]: "Laukumveida objekts"
};

const getSummaryId = (oid: string, LvmForestryData: ILVMForestryData) => {
    const date = new Date(Date.now());
    const year = date.getUTCFullYear();
    const id =
        year +
        "_" +
        LVMForestryByCode[LvmForestryData.LVMForestryCode] +
        (LvmForestryData.LVMForestryCount && LvmForestryData.LVMForestryCount > 1 ? "-V" : "") +
        "_PS_" +
        oid;
    console.log("id", id);
    return id;
};

const LVMForestryByCode = {
    1: "AV", // "Austrumvidzemes reģions"
    2: "DK", // "Dienvidkurzemes reģions"
    3: "DL", // "Dienvidlatgales reģions"
    4: "RV", // "Rietumvidzemes reģions"
    5: "VD", // "Vidusdaugavas reģions"
    6: "ZG", // "Zemgales reģions"
    7: "ZK", // "Ziemeļkurzemes reģions"
    8: "ZL" // "Ziemeļlatgales reģions"
};

const resetMinMaxDates = () =>
    config.forms.eventForm.fields.map((f) => {
        f.minDate && (f.minDate = undefined);
        f.maxDate && (f.maxDate = undefined);
    });
